import {
  Component,
  AfterViewInit,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
  effect,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core'
import { CategoryTreeType } from '../../models'
import { DndDropEvent } from 'ngx-drag-drop'
import { Category } from 'depoto-core/src/entities'
import { CoreService, NotificationService } from '../../services'
import { TranslateService } from '@ngx-translate/core'
import { SchemaUtil } from '../../utils'
import { injectMutation, injectQuery } from '@tanstack/angular-query-experimental'
import { CategoryService } from '../../services/entity-services/category.service'

@Component({
  selector: 'category-tree-row',
  templateUrl: './category-tree-row.component.html',
  styleUrls: ['./category-tree-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CategoryTreeRowComponent implements OnInit, AfterViewInit, OnChanges {
  private categoryService = inject(CategoryService)

  @ViewChild('arrowIconRadioParentCategory') arrowIconRadioParentCategory: ElementRef
  @ViewChild('arrowIconRadioProductMainCategory') arrowIconRadioProductMainCategory: ElementRef
  @ViewChild('arrowIconCheckboxProductCategories') arrowIconCheckboxProductCategories: ElementRef

  @Input() public editedCategory: Category | Partial<Category>
  @Output() editedCategoryChange: EventEmitter<any> = new EventEmitter()

  @Input() public mainCategory: Category | Partial<Category>
  @Output() mainCategoryChange: EventEmitter<any> = new EventEmitter()

  @Input() selectedCategories: Category[] | Partial<Category>[]
  @Output() selectedCategoriesChange: EventEmitter<any> = new EventEmitter()

  @Input() public category: Category | Partial<Category>
  @Input() public type: CategoryTreeType

  @Input() isInBulkEdits = false
  @Input() isForFilterCategories = false

  public categoryTreeTypeEnum = CategoryTreeType
  public isShow = true
  public showSubcategories = false
  public checkedCategory: number | boolean = false

  // Control when the subcategories query is allowed to run
  private subcatsEnabled = signal(false)

  // public subcategories: Category[] | Partial<Category>[] = []
  readonly querySubcategories = injectQuery(() => ({
    queryKey: ['subcategories', this.category.id],
    queryFn: async () => {
      // if (!this.type) {
      //   alert(this.translateService.instant('category-tree.unknown'))
      // }
      // direction: 'ASC' todo ?
      return await this.categoryService
        .getCategories({ filters: { parent: this.category.id } }, SchemaUtil.category)
        .then(result => result.items)
    },
    // Do not auto-run; wait until user expands (controlled via signal)
    enabled: this.subcatsEnabled(),
  }))

  deleteCategory = injectMutation(() => ({
    mutationFn: async () => {
      if (!confirm(this.translateService.instant('category-tree-row.delete.confirmation'))) {
        return new Promise<void>(r => r())
      }
      return await this.core.services.category.delete(this.category)
      // if err -> this.isShow = true
    },
    onSuccess: () => {
      this.isShow = false
      this.notificationService.success(this.translateService.instant('alert.category.deleted'))
    },
  }))

  constructor(
    private core: CoreService,
    private notificationService: NotificationService,
    private translateService: TranslateService
  ) {
    effect(() => {
      // this.querySubcategories.data()
    })
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.editedCategory || changes.selectedCategories || changes.category || changes.type) {
      this.syncCheckedCategory()
    }
  }

  ngOnInit() {
    if (this.type === this.categoryTreeTypeEnum.radioParentCategory) {
      this.syncCheckedCategory()
    } else if (this.type === this.categoryTreeTypeEnum.radioProductMainCategory) {
      this.mainCategory ? (this.checkedCategory = this.mainCategory.id) : (this.checkedCategory = null)
    } else if (this.type === this.categoryTreeTypeEnum.checkboxProductCategories) {
      this.syncCheckedCategory()
    }
  }

  ngAfterViewInit() {
    if (this.type === this.categoryTreeTypeEnum.radioParentCategory && this.isForSubcategoryShow('parentCategory')) {
      this.arrowIconRadioParentCategory.nativeElement.click()
    } else if (
      this.type === this.categoryTreeTypeEnum.radioProductMainCategory &&
      this.isForSubcategoryShow('productMainCategory')
    ) {
      this.arrowIconRadioProductMainCategory.nativeElement.click()
    } else if (
      this.type === this.categoryTreeTypeEnum.checkboxProductCategories &&
      this.isForSubcategoryShow('productCategories')
    ) {
      const hasSelectedSubcategory = this.selectedCategories.some(category =>
        this.isSelectedCategorySubcategory(category)
      )
      if (hasSelectedSubcategory) {
        this.arrowIconCheckboxProductCategories.nativeElement.click()
      }
    }
  }

  public async subcategoriesShow(): Promise<any> {
    // Toggle UI first
    this.showSubcategories = !this.showSubcategories
    // Enable and fetch on first expand; do nothing when collapsing
    if (this.showSubcategories) {
      if (!this.subcatsEnabled()) this.subcatsEnabled.set(true)
      // Ensure data is loaded when panel opens
      this.querySubcategories.refetch()
    }
  }
  // public async subcategoriesShow(): Promise<any> {
  //   if (this.querySubcategories.data() && this.querySubcategories.data().length > 0) {
  //     this.showSubcategories ? (this.showSubcategories = false) : (this.showSubcategories = true)
  //   }
  // }

  public unsetCategory(radioInput: HTMLInputElement): void {
    this.checkedCategory = null
    radioInput.checked = false
    if (this.type === this.categoryTreeTypeEnum.radioParentCategory) {
      this.editedCategoryChanged({ ...this.editedCategory, parent: null })
    } else if (this.type === this.categoryTreeTypeEnum.radioProductMainCategory) {
      this.mainCategory = null
      this.mainCategoryChange.emit(this.mainCategory)
    }
  }

  public onNgModelChange(category: Category): void {
    if (this.type === this.categoryTreeTypeEnum.radioParentCategory) {
      this.editedCategoryChanged({ ...this.editedCategory, parent: category })
    } else if (this.type === this.categoryTreeTypeEnum.radioProductMainCategory) {
      this.mainCategory = category
      this.mainCategoryChange.emit(this.mainCategory)
    }
  }

  editedCategoryChanged(category: Category | Partial<Category>): void {
    this.editedCategory = category
    this.syncCheckedCategory()
    // A selection in a descendant row must reach the category form at the top of the tree.
    this.editedCategoryChange.emit(category)
  }

  public async onNgModelChangeCheckbox(value: boolean, category: Category | Partial<Category>): Promise<void> {
    if (this.isForFilterCategories) {
      await this.onFilterCategoryChange(value, category)
      return
    }

    if (value) {
      this.selectedCategories.push(category as Category)
      this.selectedCategoriesChange.emit(this.selectedCategories)
    } else {
      this.selectedCategories = this.selectedCategories.filter(
        (categoryInArray: Category | Partial<Category>) => categoryInArray.id !== category.id
      )
      this.selectedCategoriesChange.emit(this.selectedCategories)
    }
  }

  selectedCategoriesChanged(event: any) {
    this.selectedCategories = event
    this.selectedCategoriesChange.emit(this.selectedCategories)
  }

  private async onFilterCategoryChange(value: boolean, category: Category | Partial<Category>): Promise<void> {
    try {
      const categorySubtree = await this.getCategorySubtree(category)
      if (value) {
        this.selectedCategories = this.mergeCategories(this.selectedCategories || [], categorySubtree)
        this.checkedCategory = true
      } else {
        const categoryIds = new Set(categorySubtree.map(categoryInSubtree => categoryInSubtree.id))
        this.selectedCategories = (this.selectedCategories || []).filter(
          categoryInArray => !categoryIds.has(categoryInArray.id)
        )
        this.checkedCategory = false
      }
      this.selectedCategoriesChange.emit(this.selectedCategories)
    } catch {
      this.syncCheckedCategory()
    }
  }

  private async getCategorySubtree(category: Category | Partial<Category>): Promise<Category[]> {
    if (!category?.hasChildren) {
      return [category as Category]
    }

    const subcategories = await this.categoryService
      .getCategories({ filters: { parent: category.id } }, SchemaUtil.category)
      .then(result => result.items as unknown as Category[])
    const subcategoryTrees = await Promise.all(subcategories.map(subcategory => this.getCategorySubtree(subcategory)))

    return [category as Category, ...subcategoryTrees.flat()]
  }

  private mergeCategories(
    selectedCategories: Category[] | Partial<Category>[],
    categoriesToAdd: Category[]
  ): Category[] {
    const categoryMap = new Map<number, Category | Partial<Category>>()
    ;[...selectedCategories, ...categoriesToAdd].forEach(category => {
      if (category?.id && !categoryMap.has(category.id)) {
        categoryMap.set(category.id, category)
      }
    })

    return Array.from(categoryMap.values()) as Category[]
  }

  private syncCheckedCategory(): void {
    if (this.type === this.categoryTreeTypeEnum.radioParentCategory) {
      this.checkedCategory = this.editedCategory?.parent?.id ?? null
      return
    }

    if (this.type !== this.categoryTreeTypeEnum.checkboxProductCategories) {
      return
    }

    this.checkedCategory = !!this.selectedCategories?.some(category => category.id === this.category?.id)
  }

  private isSelectedCategorySubcategory(category: Category | Partial<Category>): boolean {
    return (
      category.treePath?.indexOf(this.category.id) > -1 &&
      category.treePath[category.treePath.length - 1] !== this.category.id
    )
  }

  private isForSubcategoryShow(type: 'parentCategory' | 'productMainCategory' | 'productCategories'): boolean {
    if (type === 'parentCategory') {
      return (
        this.editedCategory &&
        this.editedCategory.treePath &&
        this.editedCategory.treePath.indexOf(this.category.id) > -1 &&
        this.editedCategory.treePath[this.editedCategory.treePath.length - 2] !== this.category.id &&
        this.editedCategory.treePath[this.editedCategory.treePath.length - 1] !== this.category.id
      )
    } else if (type === 'productMainCategory') {
      return (
        this.mainCategory?.treePath &&
        this.mainCategory.treePath.indexOf(this.category.id) > -1 &&
        this.mainCategory.treePath[this.mainCategory.treePath.length - 1] !== this.category.id
      )
    } else if (type === 'productCategories') {
      return this.selectedCategories?.length > 0
    }
  }

  public isRadioSelected(radioInput: HTMLInputElement): boolean {
    return radioInput.checked
  }

  public async onDrop(event: DndDropEvent, list: Category[], parentCategory: Category) {
    if (list) {
      let index = event.index
      if (typeof index === 'undefined') {
        index = list.length
      }
      const droppedCategory: Category = event.data
      list.splice(index, 0, droppedCategory)
      droppedCategory.parent = new Category(parentCategory)
      droppedCategory.position = index
      await this.core.services.category.update(droppedCategory)
    }
  }

  public onDragMove(item: Category, list: Category[]) {
    const index = list.indexOf(item)
    list.splice(index, 1)
  }
}
